import { Autocomplete, Chip, ChipDelete, IconButton, Tooltip } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type CompanyFormValues } from '@/containers/Companies/AddEditCompany/useAddEditCompanyFormConfig';
import { type ContactFormValues } from '@/containers/Contacts/AddEditContact/useContactForm';
import { type RelationOption } from '@/types/trpc/case';

import { RelationType } from '~/common/enums';
import { trpc } from '@/config/trpc';

import { createMultiSort, sortWithCustomOrder } from '~/common/utils/sortUtils';

import { useCreateCompany, useCreateContact } from '@/mutations';
import useNotification from '@/shared/hooks/UseNotification';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';
import { useAutocompleteOptions } from '@/shared/hooks/autocomplete';

import { ClearIcon, CompanyIcon, ContactIcon, InlineSpinner } from '@/shared/icons/Icons';
import FormField, { type FieldProps } from '../input/FormField';
import AddEditCompanyForm from '@/containers/Companies/AddEditCompany/AddEditCompanyForm';
import AddEditContactForm from '@/containers/Contacts/AddEditContact/AddEditContactForm';
import KpcModal from '../layout/KpcModal';
import VirtualListBoxAdapter from '../adapters/VirtualListBoxAdapter';

const OPTIONS_SORT_ORDER = [RelationType.Contact, RelationType.Team, RelationType.Company];

export type CaseRelationOption = RelationOption & {
  isSuggestion: boolean;
};

const sortOptions = createMultiSort(
  {
    key: 'isSuggestion',
    sortFn: (a: boolean, b: boolean) => Number(b) - Number(a),
  },
  {
    key: 'type',
    order: OPTIONS_SORT_ORDER,
    sortFn: sortWithCustomOrder,
  },
  {
    key: 'label',
    sortFn: (a: string, b: string) => a.localeCompare(b),
  },
);

type UseCaseRelationOptionsArgs = {
  callbacks?: {
    onError?: (error: unknown) => void;
    onSuccess?: (option: CaseRelationOption) => void;
  };
  suggestedRelationIds?: string[];
  toggles: {
    company: UseToggle;
    contact: UseToggle;
  };
};

type BaseProps = FieldProps &
  Omit<UseCaseRelationOptionsArgs, 'toggles'> & {
    onBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;
    forceSelected?: string[];
  };

const useCaseRelationOptions = ({
  callbacks,
  suggestedRelationIds,
  toggles,
}: UseCaseRelationOptionsArgs) => {
  const { sendNotification, sendSuccessMessage } = useNotification();
  const { t } = useTranslation(['common', 'errors']);

  const trpcUtils = trpc.useUtils();

  const { data: options, isLoading } = trpc.case.relation.autocompleteAll.useQuery();

  const { mutateAsync: createCompanyAsync, isLoading: isCreatingCompany } = useCreateCompany();
  const { mutateAsync: createContactAsync, isLoading: isCreatingContact } = useCreateContact();

  const orderedOptions = useMemo(() => {
    if (!options) return [];

    const groupedOptions = options.map((option) => ({
      ...option,
      isSuggestion: !!suggestedRelationIds?.includes(option.id),
    }));

    const sortedOptions = sortOptions(groupedOptions);

    return sortedOptions;
  }, [options, suggestedRelationIds]);

  const updateQueryCache = useCallback(
    (newOption: CaseRelationOption) => {
      trpcUtils.case.relation.autocompleteAll.setData(undefined, (queryData) => {
        if (!queryData) return undefined;

        return [...queryData, newOption];
      });
      trpcUtils.case.relation.autocompleteAll.invalidate();
    },
    [trpcUtils.case.relation.autocompleteAll],
  );

  const submitCompany = useCallback(
    async (company: CompanyFormValues) => {
      const type = t('company');

      try {
        const companyRes = await createCompanyAsync(company);

        const { displayName, emailAddresses, id, vatNumber, ...rest } = companyRes;

        const simplifiedEmails = emailAddresses.map((email) => email.email);

        const newCompanyOption = {
          ...companyRes,
          address: {
            country: 'BE' as const,
            postalCode: null,
          },
          displayName: displayName || id,
          emailAddresses: simplifiedEmails,
          id,
          isSuggestion: false,
          label: vatNumber ? `${displayName} (${vatNumber})` : displayName || id,
          type: RelationType.Company as const,
          value: id,
        };

        updateQueryCache(newCompanyOption);
        sendSuccessMessage(t('typeSuccessfullyAdded', { type }));
        toggles.company.hide();
        callbacks?.onSuccess?.(newCompanyOption);
      } catch (err) {
        sendNotification({ message: t('errors:failedToCreateType', { type }), variant: 'error' });
        callbacks?.onError?.(err);
      }
    },
    [
      callbacks,
      createCompanyAsync,
      sendNotification,
      sendSuccessMessage,
      t,
      toggles,
      updateQueryCache,
    ],
  );

  const submitContact = useCallback(
    async (contactValues: ContactFormValues) => {
      const type = t('contact');

      try {
        const { addressLine, city, country, postalCode, ...rest } = contactValues;

        const contactRes = await createContactAsync({
          ...rest,
          address: {
            addressLine,
            city,
            country,
            postalCode,
          },
        });

        const label = [contactRes.firstName, contactRes.lastName].filter(Boolean).join(' ').trim();

        const newContactOption = {
          ...contactRes,
          id: contactRes.id,
          isSuggestion: false,
          label,
          type: RelationType.Contact as const,
          value: contactRes.id,
        };

        updateQueryCache(newContactOption);
        sendSuccessMessage(t('typeSuccessfullyAdded', { type }));
        toggles.contact.hide();
        callbacks?.onSuccess?.(newContactOption);
      } catch (err) {
        callbacks?.onError?.(err);
      }
    },
    [callbacks, createContactAsync, sendSuccessMessage, t, toggles, updateQueryCache],
  );

  return {
    company: {
      isSubmitting: isCreatingCompany,
      submit: submitCompany,
    },
    contact: {
      isSubmitting: isCreatingContact,
      submit: submitContact,
    },
    isLoading,
    options: orderedOptions,
  };
};

type SingleProps = BaseProps & {
  multiple?: false;
  onChange: (value: CaseRelationOption | null) => void;
  selected: string | null;
};

type MultiProps = BaseProps & {
  multiple: true;
  onChange: (value: CaseRelationOption[]) => void;
  selected: string[];
};

type Props = SingleProps | MultiProps;

const CaseRelationsAutocomplete: React.FC<Props> = ({
  callbacks,
  forceSelected,
  multiple,
  onBlur,
  onChange,
  selected,
  suggestedRelationIds,
  ...formFieldProps
}) => {
  const toggles = {
    company: useToggle(),
    contact: useToggle(),
  };

  const { company, contact, isLoading, options } = useCaseRelationOptions({
    callbacks,
    suggestedRelationIds,
    toggles,
  });
  const { t } = useTranslation();

  const dropOption = (selectedOptions: CaseRelationOption[], id: string) => {
    const newOptions = selectedOptions.filter((option) => option.value !== id);

    if (multiple) {
      onChange(newOptions);
    } else {
      onChange(null);
    }
  };

  const handleCallback = (newOptions: CaseRelationOption[] | CaseRelationOption | null) => {
    const multipleOptions = Array.isArray(newOptions);

    if (multiple && multipleOptions) {
      onChange(newOptions);
    } else if (!multiple && !multipleOptions) {
      onChange(newOptions);
    }
  };

  const autocompleteProps = useAutocompleteOptions({
    callback: handleCallback,
    data: options,
    isLoading,
    multiple,
    selected,
  });

  const selectedValue = useMemo(() => {
    if (!selected) return multiple ? [] : null;

    if (Array.isArray(selected)) {
      return selected
        .map((id) => {
          const foundByValue = options.find((x) => x.value === id);
          if (foundByValue) return foundByValue;

          return options.find((x) => x.type === RelationType.Team && x.companyId === id);
        })
        .filter((x): x is CaseRelationOption => !!x);
    }

    const foundByValue = options.find((x) => x.value === selected);
    if (foundByValue) return foundByValue;

    return options.find((x) => x.type === RelationType.Team && x.companyId === selected) || null;
  }, [options, selected, multiple]);

  return (
    <>
      <FormField {...formFieldProps}>
        <div className="flex gap-3">
          <Autocomplete
            {...autocompleteProps}
            value={selectedValue}
            className="flex-grow"
            groupBy={(option) => (option.isSuggestion ? t('suggestions') : t('options'))}
            placeholder={t('search.placeholder')}
            slots={{ listbox: VirtualListBoxAdapter }}
            startDecorator={autocompleteProps.loading && <InlineSpinner />}
            getOptionDisabled={(option) => !!forceSelected?.includes(option.value)}
            onBlur={onBlur}
            renderGroup={(params) => params as unknown as React.ReactNode}
            renderOption={(props, option) => [props, option] as React.ReactNode}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  color="neutral"
                  disabled={!!forceSelected?.includes(option.value)}
                  variant="soft"
                  endDecorator={
                    <ChipDelete
                      className="mr-0.5"
                      color="neutral"
                      onClick={() => dropOption(value, option.value)}
                    >
                      <ClearIcon />
                    </ChipDelete>
                  }
                >
                  {option.label}
                </Chip>
              ))
            }
          />
          <Tooltip arrow title={t('_contact.actions.create')}>
            <IconButton onClick={toggles.contact.show}>
              <ContactIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('_company.actions.create')}>
            <IconButton onClick={toggles.company.show}>
              <CompanyIcon />
            </IconButton>
          </Tooltip>
        </div>
      </FormField>
      {toggles.company.value && (
        <KpcModal title={t('company')} toggle={toggles.company}>
          <AddEditCompanyForm
            isSubmitting={company.isSubmitting}
            onCancel={toggles.company.hide}
            onSubmit={company.submit}
          />
        </KpcModal>
      )}
      {toggles.contact.value && (
        <KpcModal title={t('contact')} toggle={toggles.contact}>
          <AddEditContactForm
            isSubmitting={contact.isSubmitting}
            onCancel={toggles.contact.hide}
            onSubmit={contact.submit}
          />
        </KpcModal>
      )}
    </>
  );
};

export default CaseRelationsAutocomplete;
